import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { TABULAR } from '../tokens/type.js';
import { TableSkeleton } from './skeleton.js';
function compare(a, b) {
    if (a == null && b == null)
        return 0;
    if (a == null)
        return 1;
    if (b == null)
        return -1;
    if (a instanceof Date && b instanceof Date)
        return a.getTime() - b.getTime();
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}
/**
 * A client-side table with typed columns (§6 tables: 13px rows, 12/500 head on `subtle`,
 * hairline-2 separators, numeric cells right-aligned and tabular). Sortable headers are
 * real buttons and announce `aria-sort`; the loading state is a skeleton, never a spinner.
 */
export function DataTable({ columns, rows, getRowId, label, loading = false, emptyState, defaultSort, onRowClick, selectedRowId, dense = false, plain = false, }) {
    const [sort, setSort] = useState(defaultSort ?? null);
    const sorted = useMemo(() => {
        if (!sort)
            return rows;
        const column = columns.find((c) => c.id === sort.columnId);
        if (!column?.sortValue)
            return rows;
        const get = column.sortValue;
        const sign = sort.direction === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => sign * compare(get(a), get(b)));
    }, [rows, sort, columns]);
    const toggleSort = (columnId) => {
        setSort((prev) => prev?.columnId === columnId
            ? { columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { columnId, direction: 'asc' });
    };
    if (loading)
        return _jsx(TableSkeleton, { rows: dense ? 5 : 8, columns: columns.length });
    const table = (_jsxs(TableContainer, { children: [_jsxs(Table, { size: dense ? 'small' : 'medium', "aria-label": label, children: [_jsx(TableHead, { children: _jsx(TableRow, { children: columns.map((column) => {
                                const sortable = Boolean(column.sortValue) && !column.actions;
                                const active = sort?.columnId === column.id;
                                return (_jsx(TableCell, { align: column.numeric ? 'right' : 'left', sx: { width: column.actions ? 48 : column.width }, ...(sortable && active
                                        ? { 'aria-sort': sort.direction === 'asc' ? 'ascending' : 'descending' }
                                        : {}), children: column.actions ? (_jsx(Box, { component: "span", sx: visuallyHidden, children: "Actions" })) : sortable ? (_jsx(TableSortLabel, { active: active, direction: active ? sort.direction : 'asc', onClick: () => toggleSort(column.id), children: column.header })) : (column.header) }, column.id));
                            }) }) }), _jsx(TableBody, { children: sorted.map((row) => {
                            const id = getRowId(row);
                            const clickable = Boolean(onRowClick);
                            return (_jsx(TableRow, { hover: clickable, selected: selectedRowId === id, onClick: clickable ? () => onRowClick?.(row) : undefined, sx: clickable ? { cursor: 'pointer' } : undefined, children: columns.map((column) => (_jsx(TableCell, { align: column.numeric ? 'right' : 'left', sx: column.numeric ? TABULAR : column.actions ? { py: 0.5 } : undefined, onClick: column.actions ? (e) => e.stopPropagation() : undefined, children: column.cell(row) }, column.id))) }, id));
                        }) })] }), sorted.length === 0 && emptyState && _jsx(Box, { sx: { p: 1 }, children: emptyState })] }));
    return plain ? table : _jsx(MuiCard, { children: table });
}
// Explicit px strings: in `sx`, `width: 1` would mean 100%.
const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
};
//# sourceMappingURL=data-table.js.map